// Auth screen: email/password (sign in or create account) plus Google sign-in.
// On success the AuthProvider's onAuthStateChanged ensures the user doc and
// reads custom claims; routing then sends the user into the shell.

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function friendly(code) {
  if (code.includes('invalid-credential') || code.includes('wrong-password'))
    return 'Incorrect email or password.';
  if (code.includes('email-already-in-use')) return 'That email already has an account.';
  if (code.includes('weak-password')) return 'Password should be at least 6 characters.';
  if (code.includes('invalid-email')) return 'That email address looks invalid.';
  if (code.includes('popup-closed')) return 'Sign-in window closed before completing.';
  return 'Something went wrong. Please try again.';
}

export default function Login() {
  const { user, loading, signInEmail, signUpEmail, signInGoogle } = useAuth();
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  if (loading) return <div className="loading">Loading…</div>;
  if (user) return <Navigate to="/dashboard" replace />;

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      if (mode === 'signin') await signInEmail(email, password);
      else await signUpEmail(email, password);
    } catch (e) {
      const code = e instanceof Error ? e.message : '';
      setErr(friendly(code));
    } finally {
      setBusy(false);
    }
  }

  async function google() {
    setBusy(true);
    setErr(null);
    try {
      await signInGoogle();
    } catch (e) {
      setErr(friendly(e instanceof Error ? e.message : ''));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card card">
        <div className="brand">
          theuth<span className="tick">/</span>prep
        </div>
        <p className="muted" style={{ marginTop: 0 }}>
          {mode === 'signin' ? 'Sign in to your study workspace.' : 'Create your account.'}
        </p>

        <div className="stack">
          <div>
            <label className="eyebrow" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className="field"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          </div>
          <div>
            <label className="eyebrow" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              className="field"
              type="password"
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          </div>

          {err && <div className="error-note">{err}</div>}

          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={busy || !email || !password}
          >
            {busy ? 'Working…' : mode === 'signin' ? 'Sign in' : 'Create account'}
          </button>
        </div>

        <div className="divider">or</div>

        <button className="btn" style={{ width: '100%' }} onClick={google} disabled={busy}>
          Continue with Google
        </button>

        <p className="muted" style={{ textAlign: 'center', marginBottom: 0, fontSize: '0.85rem' }}>
          {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setMode(mode === 'signin' ? 'signup' : 'signin');
              setErr(null);
            }}
          >
            {mode === 'signin' ? 'Create one' : 'Sign in'}
          </button>
        </p>
      </div>
    </div>
  );
}
